import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Slider } from "antd";
import zoomInSvg from "../../../../assets/icons/zoom_in.svg";
import "./CustomImageCrop.scss";

interface CustomImageCropProps {
  visible: boolean;
  onCancel: () => void;
  onApply: (croppedImage: string) => void;
  imageUrl: string;
  title: string;
  isMobile?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const CustomImageCrop: React.FC<CustomImageCropProps> = ({
  visible,
  onCancel,
  onApply,
  imageUrl,
  title,
  isMobile = false,
  onLoadingChange,
}) => {
  const [cropArea, setCropArea] = useState<CropArea>({
    x: 0,
    y: 0,
    width: 200,
    height: 200,
  });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [minZoom, setMinZoom] = useState(1);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Desktop fixed crop dimensions
  const DESKTOP_CROP_WIDTH = 1100;
  const DESKTOP_CROP_HEIGHT = 450;

  // Mobile aspect ratio (9:16 portrait)
  const MOBILE_ASPECT_RATIO = 9 / 16;

  useEffect(() => {
    if (visible && imageUrl) {
      // Notify parent component that loading has started
      onLoadingChange?.(true);

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        if (imageRef.current && containerRef.current) {
          imageRef.current.src = imageUrl;
          setImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
          // Delay initialization to ensure container is rendered
          setTimeout(() => {
            calculateMinZoom(img.naturalWidth, img.naturalHeight);
            initializeCropArea();
            // Notify parent component that loading has finished
            onLoadingChange?.(false);
          }, 100);
        }
      };
      img.onerror = () => {
        onLoadingChange?.(false);
      };
      img.src = imageUrl;
    }
  }, [visible, imageUrl, onLoadingChange]);

  // Validate crop box bounds on zoom or container resize
  useEffect(() => {
    if (!containerRef.current || !isMobile) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    setCropArea((prev) => {
      // Ensure crop box stays within container bounds
      const minX = 0;
      const maxX = containerWidth - prev.width;
      const minY = 0;
      const maxY = containerHeight - prev.height;

      const clampedX = Math.max(minX, Math.min(prev.x, maxX));
      const clampedY = Math.max(minY, Math.min(prev.y, maxY));

      // Only update if position changed
      if (clampedX !== prev.x || clampedY !== prev.y) {
        return {
          ...prev,
          x: clampedX,
          y: clampedY,
        };
      }

      return prev;
    });
  }, [zoom, isMobile]);

  const calculateMinZoom = (imgWidth: number, imgHeight: number) => {
    if (!containerRef.current || imgWidth === 0 || imgHeight === 0) {
      setMinZoom(1);
      return;
    }

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = 400; // Fixed container height

    if (isMobile) {
      // Mobile: minimum zoom is fixed at 1.3
      const finalMinZoom = 1.3;
      setMinZoom(finalMinZoom);
      setZoom((prevZoom) => Math.max(prevZoom, finalMinZoom));
    } else {
      // Desktop: crop box is full width, 300px height
      const cropHeight = 300;

      // Calculate the aspect ratios
      const containerRatio = containerWidth / containerHeight;
      const imageRatio = imgWidth / imgHeight;

      // For object-fit: cover, determine which dimension is limiting
      let baseDisplayWidth: number;
      let baseDisplayHeight: number;

      if (containerRatio > imageRatio) {
        // Container is wider - image height determines coverage
        baseDisplayHeight = containerHeight;
        baseDisplayWidth = containerHeight * imageRatio;
      } else {
        // Container is taller - image width determines coverage
        baseDisplayWidth = containerWidth;
        baseDisplayHeight = containerWidth / imageRatio;
      }

      // Calculate minimum zoom to ensure image covers:
      // 1. Full container width
      // 2. Crop height of 300px
      const zoomForWidth = containerWidth / baseDisplayWidth;
      const zoomForCropHeight = cropHeight / baseDisplayHeight;

      // Use the larger zoom value to ensure both dimensions are covered
      const calculatedMinZoom = Math.max(zoomForWidth, zoomForCropHeight);
      const finalMinZoom = Math.max(1, calculatedMinZoom);
      setMinZoom(finalMinZoom);
      setZoom((prevZoom) => Math.max(prevZoom, finalMinZoom));
    }
  };

  const initializeCropArea = () => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    if (isMobile) {
      // Mobile: fixed width of 240px, full height (100%)
      const width = 240;
      const height = containerHeight;

      // Center horizontally, start at top (y=0 for full height)
      setCropArea({
        x: (containerWidth - width) / 2,
        y: 0,
        width,
        height,
      });
    } else {
      // Desktop: full width, fixed height of 300px
      const width = containerWidth;
      const height = 300;

      // Center horizontally (x=0 for full width), center vertically
      setCropArea({
        x: 0,
        y: (containerHeight - height) / 2,
        width,
        height,
      });
    }
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on resize handles - COMPLETELY DISABLED
    // const handleSize = 15; // Increased handle size for better interaction
    // const {
    //   x: cropX,
    //   y: cropY,
    //   width: cropWidth,
    //   height: cropHeight,
    // } = cropArea;

    // Only allow custom resizing if Custom is selected - COMMENTED OUT
    // if (aspectRatio === "Custom") {
    //   // Corner handles
    //   if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    //   // Edge handles for custom mode
    //   else if (
    //     x >= cropX + handleSize &&
    //     x <= cropX + cropWidth - handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + handleSize &&
    //     x <= cropX + cropWidth - handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY + handleSize &&
    //     y <= cropY + cropHeight - handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY + handleSize &&
    //     y <= cropY + cropHeight - handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    // } else {
    //   // For non-custom mode, still allow corner resizing
    //   if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY - handleSize &&
    //     y <= cropY + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("top-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX - handleSize &&
    //     x <= cropX + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-left");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   } else if (
    //     x >= cropX + cropWidth - handleSize &&
    //     x <= cropX + cropWidth + handleSize &&
    //     y >= cropY + cropHeight - handleSize &&
    //     y <= cropY + cropHeight + handleSize
    //   ) {
    //     setIsResizing(true);
    //     setResizeHandle("bottom-right");
    //     e.preventDefault();
    //     e.stopPropagation();
    //   }
    // }

    // Only allow dragging if clicking inside crop area (no resizing)
    const {
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight,
    } = cropArea;

    if (
      x >= cropX &&
      x <= cropX + cropWidth &&
      y >= cropY &&
      y <= cropY + cropHeight
    ) {
      setIsDragging(true);
    }

    setDragStart({ x, y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const deltaX = x - dragStart.x;
    const deltaY = y - dragStart.y;

    if (isDragging) {
      setCropArea((prev) => {
        if (!containerRef.current) return prev;

        if (isMobile) {
          // Mobile: allow both horizontal and vertical movement
          // Use rect dimensions for consistency with coordinate system
          const containerWidth = rect.width;
          const containerHeight = rect.height;

          // With object-fit: cover and overflow: hidden, the visible area is exactly the container
          // Crop box must stay within container bounds (0 to containerWidth - cropWidth)

          // Horizontal bounds: crop box must stay within container
          const minX = 0;
          const maxX = Math.max(0, containerWidth - prev.width);

          // Vertical bounds: crop box must stay within container
          const minY = 0;
          const maxY = Math.max(0, containerHeight - prev.height);

          // Clamp crop box position to container bounds
          const newX = Math.max(minX, Math.min(prev.x + deltaX, maxX));
          const newY = Math.max(minY, Math.min(prev.y + deltaY, maxY));

          return {
            ...prev,
            x: newX,
            y: newY,
          };
        } else {
          // Desktop: only vertical movement (x stays at 0 for full width)
          const containerHeight = rect.height;
          const minY = 0;
          const maxY = Math.max(0, containerHeight - prev.height);

          return {
            ...prev,
            // x stays at 0 for full width - no horizontal movement
            y: Math.max(minY, Math.min(prev.y + deltaY, maxY)),
          };
        }
      });
    }

    setDragStart({ x, y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleReset = () => {
    setZoom(Math.max(1, minZoom));
    initializeCropArea();
  };

  const handleZoomChange = (value: number) => {
    setZoom(Math.max(minZoom, Math.min(3, value)));
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(3, prev + 0.1));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(minZoom, prev - 0.1));
  };

  const handleApply = () => {
    if (!canvasRef.current || !imageRef.current || !containerRef.current)
      return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { x, y, width, height } = cropArea;
    const img = imageRef.current;
    const container = containerRef.current;

    // Get container and image dimensions
    const containerRect = container.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    // Calculate scale between displayed and natural image size
    const scaleX = imageDimensions.width / imgRect.width;
    const scaleY = imageDimensions.height / imgRect.height;

    // Since image is object-fit: cover, calculate the actual visible area
    const imgDisplayRatio = imgRect.width / imgRect.height;
    const imgNaturalRatio = imageDimensions.width / imageDimensions.height;

    // Calculate image position within container
    const imgDisplayX = imgRect.left - containerRect.left;
    const imgDisplayY = imgRect.top - containerRect.top;

    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = imageDimensions.width;
    let sourceHeight = imageDimensions.height;

    // Adjust for object-fit: cover behavior
    if (imgDisplayRatio > imgNaturalRatio) {
      // Image is cropped vertically (top/bottom cut off)
      // Visible height is determined by image width fitting container width
      const visibleHeight = imageDimensions.width / imgDisplayRatio;
      const cropTop = (imageDimensions.height - visibleHeight) / 2;

      // Crop box y is relative to container, convert to image-relative
      const cropBoxYRelativeToImage = y - imgDisplayY;
      sourceY = cropTop + cropBoxYRelativeToImage * scaleY;
      sourceHeight = height * scaleY;

      // For mobile, account for horizontal position
      if (isMobile) {
        // Visible width is full image width (since height determines visible area)
        // Crop box x is relative to container, convert to image-relative
        const cropBoxXRelativeToImage = x - imgDisplayX;
        sourceX = cropBoxXRelativeToImage * scaleX;
        sourceWidth = width * scaleX;
      }
    } else {
      // Image is cropped horizontally (left/right cut off)
      // Visible width is determined by image height fitting container height
      const visibleWidth = imageDimensions.height * imgDisplayRatio;
      const cropLeft = (imageDimensions.width - visibleWidth) / 2;

      if (isMobile) {
        // Mobile: crop box x is relative to container, convert to image-relative
        const cropBoxXRelativeToImage = x - imgDisplayX;
        sourceX = cropLeft + cropBoxXRelativeToImage * scaleX;
        sourceWidth = width * scaleX;
      } else {
        // Desktop: full width, so use cropLeft
        sourceX = cropLeft;
        sourceWidth = width * scaleX;
      }

      // Crop box y is relative to container, convert to image-relative
      const cropBoxYRelativeToImage = y - imgDisplayY;
      sourceY = cropBoxYRelativeToImage * scaleY;
      sourceHeight = height * scaleY;
    }

    // Clamp to image bounds
    sourceX = Math.max(0, Math.min(sourceX, imageDimensions.width));
    sourceY = Math.max(0, Math.min(sourceY, imageDimensions.height));
    sourceWidth = Math.min(sourceWidth, imageDimensions.width - sourceX);
    sourceHeight = Math.min(sourceHeight, imageDimensions.height - sourceY);

    // Set output dimensions
    // Mobile: 240px width, actual height (container height)
    // Desktop: fixed 1100x450
    const outputWidth = isMobile ? 240 : DESKTOP_CROP_WIDTH;
    const outputHeight = isMobile ? height : DESKTOP_CROP_HEIGHT;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Draw the cropped image
    ctx.drawImage(
      img,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    const croppedImageUrl = canvas.toDataURL("image/jpeg", 0.9);
    onApply(croppedImageUrl);
  };

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
      className="custom-crop-modal"
    >
      <div className="crop-container">
        <div
          ref={containerRef}
          className="image-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Crop"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: "center",
              pointerEvents: "none", // Prevent image from interfering with crop interactions
            }}
          />

          <div
            className="crop-overlay"
            style={{
              left: cropArea.x,
              top: cropArea.y,
              width: cropArea.width,
              height: cropArea.height,
              border: "2px solid #1890ff", // Simple border to show crop area
              backgroundColor: "rgba(24, 144, 255, 0.1)", // Subtle background
              cursor: isMobile ? "move" : "ns-resize", // Move cursor for mobile (both directions), vertical-only for desktop
            }}
          >
            {/* Removed all crop handles - only visual crop area remains */}
          </div>
        </div>

        <div className="crop-controls">
          <div className="control-section zoom-section">
            <div className="control-header">
              <img src={zoomInSvg} alt="Zoom" className="control-icon" />
              <label>Zoom</label>
            </div>
            <div className="control-separator"></div>
            <div className="zoom-controls">
              <span className="zoom-minus" onClick={handleZoomOut}>
                -
              </span>
              <Slider
                min={minZoom}
                max={3}
                step={0.1}
                value={zoom}
                onChange={handleZoomChange}
                className="zoom-slider"
                tipFormatter={(value) =>
                  value ? Math.round(value * 10) / 10 : 0
                }
              />
              <span className="zoom-plus" onClick={handleZoomIn}>
                +
              </span>
            </div>
          </div>
        </div>

        <div className="crop-actions">
          <Button onClick={handleReset}>Reset</Button>
          <Button type="primary" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </Modal>
  );
};

export default CustomImageCrop;
